import React, { useState } from 'react';
import { countries, REGION_ORDER, REGION_LABELS, OFFSITE_RATES } from '../data/countries';
import CountryPicker from './CountryPicker';

/**
 * Per-country cheat sheet.
 *
 * Pick a country at the top → the Etsy fees section re-derives from the
 * canonical data in src/data/countries.js. The Jun 22, 2026 regulatory
 * fee changes auto-apply on/after that date via effectiveRegulatory(),
 * which mirrors the helper in src/lib/calculations.js so the cheatsheet
 * never drifts from the actual calculator.
 *
 * "Margins & pricing rules of thumb" stay universal — they're advice,
 * not country data.
 */

// Same flip-date helper as in lib/calculations.js. Kept inline so this
// component has zero coupling to the calc engine.
const REGULATORY_FLIP_TIMESTAMP_UTC = Date.UTC(2026, 5, 22); // Jun 22, 2026 UTC
function effectiveRegulatory(country, now = Date.now()) {
  if (
    now >= REGULATORY_FLIP_TIMESTAMP_UTC &&
    typeof country.regulatoryAfterJun22_2026 === 'number'
  ) {
    return country.regulatoryAfterJun22_2026;
  }
  return country.regulatory;
}

// Date the country fee data was last reviewed against Etsy's published
// rates. Bump this when you update src/data/countries.js.
const LAST_VERIFIED = 'April 2026';

export default function CheatSheet({ defaultCountry = 'US' }) {
  const [countryCode, setCountryCode] = useState(defaultCountry);
  const c = countries[countryCode] || countries.US;
  const reg = effectiveRegulatory(c);
  const offsiteUnder = (OFFSITE_RATES.under10k * 100).toFixed(0);
  const offsiteOver = (OFFSITE_RATES.over10k * 100).toFixed(0);

  // Country-specific Etsy fee rows
  const etsyFeeSection = {
    title: 'Etsy fees · ' + c.name,
    accent: '#1e3a8a',
    rows: [
      {
        icon: '🧾',
        label: 'Listing',
        value: '$0.20',
        hint: 'per item, every 4 months (universal)',
      },
      {
        icon: '💳',
        label: 'Transaction',
        value: '6.5%',
        hint: 'of order total incl. shipping (universal)',
      },
      {
        icon: '🔁',
        label: 'Processing',
        value: `${(c.processing.pct * 100).toFixed(1)}% + ${c.symbol}${c.processing.fixed.toFixed(2)}`,
        hint: c.payoneer ? 'paid via Payoneer' : `paid in ${c.code}`,
      },
      reg > 0
        ? {
            icon: '🏛️',
            label: 'Regulatory',
            value: `${(reg * 100).toFixed(2)}%`,
            hint: c.regulatoryAfterJun22_2026 && Date.now() < REGULATORY_FLIP_TIMESTAMP_UTC
              ? `→ ${(c.regulatoryAfterJun22_2026 * 100).toFixed(2)}% on Jun 22, 2026`
              : 'digital services tax pass-through',
          }
        : {
            icon: '🏛️',
            label: 'Regulatory',
            value: 'none',
            hint: 'no DST applies in this country',
          },
      {
        icon: '📡',
        label: 'Offsite Ads',
        value: `${offsiteUnder}–${offsiteOver}%`,
        hint: 'capped at $100 per order',
      },
    ],
  };

  // Universal margin/pricing rules of thumb — same for every country
  const marginsSection = {
    title: 'Margins & pricing rules of thumb',
    accent: '#047857',
    rows: [
      { icon: '📐', label: 'Healthy markup', value: '2.5× – 4×', hint: 'over direct cost' },
      { icon: '✨', label: 'Healthy margin', value: '30%+',       hint: 'leaves room for surprises' },
      { icon: '⚠️', label: 'Risky margin',   value: '< 15%',      hint: 'one bad month wipes it' },
      { icon: '⏱️', label: 'Blind spot',     value: 'Your time',  hint: 'most makers forget to price it' },
    ],
  };

  return (
    <div>
      {/* Country picker + verified status — single row, framed */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '0.85rem',
          flexWrap: 'wrap',
          padding: '0.75rem 0.9rem',
          background: 'rgba(30, 58, 138, 0.04)',
          border: '1.5px solid rgba(30, 58, 138, 0.2)',
          borderRadius: '0.625rem',
          marginBottom: '1.1rem',
        }}
      >
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <label
            htmlFor="cheat-country"
            style={{
              display: 'block',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.86rem',
              fontWeight: 600,
              color: 'rgba(26,26,46,0.7)',
              lineHeight: 1.1,
              marginBottom: '0.25rem',
            }}
          >
            🌍 Country
          </label>
          <CountryPicker
            value={countryCode}
            onChange={setCountryCode}
            countries={countries}
            regionOrder={REGION_ORDER}
            regionLabels={REGION_LABELS}
          />
        </div>

        <div style={{ flex: '0 0 auto' }}>
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.78rem',
              color: c.verified ? '#047857' : '#b45309',
              fontWeight: 600,
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
            }}
          >
            {c.verified ? '✓' : '⚠️'}{' '}
            {c.verified
              ? `Rates verified ${LAST_VERIFIED}`
              : 'Rates are best-estimates'}
          </p>
        </div>
      </div>

      {/* Two themed sections in a column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.4rem' }}>
        <SectionBlock section={etsyFeeSection} />
        <SectionBlock section={marginsSection} />
      </div>

      {/* Coverage & data freshness footer */}
      <div
        style={{
          marginTop: '1.4rem',
          padding: '0.75rem 0.9rem',
          background: 'rgba(180, 83, 9, 0.05)',
          border: '1px dashed rgba(180, 83, 9, 0.35)',
          borderRadius: '0.55rem',
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.84rem',
          color: 'rgba(26, 26, 46, 0.78)',
          lineHeight: 1.5,
        }}
      >
        <p style={{ margin: '0 0 0.35rem' }}>
          🌍 <strong>61 countries</strong> covered — every Etsy Payments market.{' '}
          📅 Future fee changes (e.g.&nbsp;the Jun&nbsp;22, 2026 regulatory bump in 7 EU markets){' '}
          <strong>auto-apply on the date</strong> they take effect — no manual update needed.
        </p>
        <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(26, 26, 46, 0.6)' }}>
          Spotted a rate that doesn't match your real Etsy payout?{' '}
          <a
            href="#contact"
            style={{ color: '#1e3a8a', fontWeight: 600, textDecoration: 'underline' }}
          >
            Tell us
          </a>{' '}
          and we'll fix the source data.
        </p>
      </div>
    </div>
  );
}

// ─── Section block ───────────────────────────────────────────────────
function SectionBlock({ section }) {
  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.55rem',
          marginBottom: '0.55rem',
        }}
      >
        <span
          style={{
            width: '4px',
            height: '14px',
            background: section.accent,
            borderRadius: '2px',
            flexShrink: 0,
          }}
          aria-hidden="true"
        />
        <h3
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.78rem',
            fontWeight: 700,
            color: section.accent,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            margin: 0,
          }}
        >
          {section.title}
        </h3>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: '0.5rem',
        }}
      >
        {section.rows.map((row) => (
          <FactCard key={row.label} {...row} accent={section.accent} />
        ))}
      </div>
    </section>
  );
}

// ─── Fact card ───────────────────────────────────────────────────────
function FactCard({ icon, label, value, hint, accent }) {
  return (
    <div
      style={{
        background: '#fefdf8',
        border: '1.5px solid rgba(26, 26, 46, 0.12)',
        borderLeft: `3px solid ${accent}`,
        borderRadius: '0.55rem',
        padding: '0.6rem 0.75rem',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.18rem' }}>
        <span aria-hidden="true" style={{ fontSize: '0.95rem', lineHeight: 1, flexShrink: 0 }}>{icon}</span>
        <span
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.7rem',
            fontWeight: 700,
            color: 'rgba(26, 26, 46, 0.6)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      </div>
      <p
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.98rem',
          fontWeight: 700,
          color: '#1a1a2e',
          letterSpacing: '-0.01em',
          lineHeight: 1.2,
          margin: '0 0 0.15rem',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </p>
      {hint && (
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.78rem',
            color: 'rgba(26, 26, 46, 0.65)',
            lineHeight: 1.35,
            margin: 0,
          }}
        >
          {hint}
        </p>
      )}
    </div>
  );
}
